<template>
  <SubscriptionNavigation
    @submit-clicked="onSubmit"
    :is-submitting-data="isSaveSubscriptionInProgress"
    :is-loading-data="isGetSubscriptionInProgress"
  >
    <div data-cy="billing-mode-page">
      <div class="text-center">
        <AtomText
          :text="t('subscription.billingMode.title')"
          font-style="font-semibold"
          font-size="text-xl"
          data-cy="billing-mode-page-title"
        />
      </div>
      <AnnualBudgetTotalCard
        v-if="getBudgetPrices?.totalAnnual"
        has-detail-button
        class="mb-8 mt-6"
        data-cy="billing-mode-annual-budget-card"
        @detail="isBudgetDetailModalOpen = true"
      />
      <BudgetDetailModal v-model:is-open="isBudgetDetailModalOpen" />
      <div v-if="isRealTimeAvailable" data-cy="billing-mode-linear-or-realtime-content">
        <AtomText
          :text="t('subscription.billingMode.linearOrRealTime.subtitle')"
          font-style="font-medium"
          font-size="text-sm"
          class="mb-2"
        />
        <OrganismRadioGroupCard
          v-model="currentDebitMode"
          name="directDebitMode"
          :cards="directDebitModeCards"
          data-cy="billing-mode-debit-mode"
          :has-error="!!debitModeError"
          :error-message="debitModeError"
        >
          <template #card-realtime>
            <img
              alt="Realtime Schema"
              src="@/assets/images/subscription/realtime-schema.svg"
              class="w-16"
            />
          </template>
          <template #card-linear>
            <img
              alt="Linear Schema"
              src="@/assets/images/subscription/linear-schema.svg"
              class="w-16"
            />
          </template>
        </OrganismRadioGroupCard>
        <MoleculeStandaloneLink
          :label="t('subscription.billingMode.linearOrRealTime.descriptionLink.title')"
          class="mt-2"
          target="blank"
          type="url"
          font-size="sm"
          font-weight="medium"
          icon-name="ExternalLinkIcon"
          :to="t('subscription.billingMode.linearOrRealTime.descriptionLink.link')"
          color-variant="brand"
        />
      </div>

      <div v-else data-cy="billing-mode-linear-only-content">
        <AtomText
          :text="t('subscription.billingMode.linearOnly.title')"
          font-style="font-medium"
          font-size="text-sm"
        />
        <AtomText
          :text="t('subscription.billingMode.linearOnly.description')"
          color-variant="description"
          font-size="text-sm"
          class="my-1"
        />
      </div>
      <MonthlyPaymentSelector v-if="hasPaymentOptionsAvailable" v-model="currentMonthlyPayment" />
    </div>
  </SubscriptionNavigation>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { useField } from 'vee-validate';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import * as yup from 'yup';

import { useStepStore, useSubscriptionStore } from '@/stores';
import useToastSnackbarStore from '@/stores/toastSnackbar.store';

import type { DirectDebitMode } from '@/types/subscription.type';
import { resolveApiErrorMessage } from '@/utils/error.util';

import MonthlyPaymentSelector from '@/components/subscription/BillingMode/MonthlyPaymentSelector.component.vue';
import AnnualBudgetTotalCard from '@/components/subscription/BudgetSchedule/AnnualBudgetTotalCard.component.vue';
import BudgetDetailModal from '@/components/subscription/BudgetSchedule/BudgetDetailModal.component.vue';
import SubscriptionNavigation from '@/components/SubscriptionNavigation.component.vue';

import { EDirectDebitErrorType } from '@/constants/errors.contants';
import { EDirectDebitMode } from '@/constants/subscription.constants';

const { t } = useI18n();

const subscriptionStore = useSubscriptionStore();
const stepStore = useStepStore();
const { addErrorToast } = useToastSnackbarStore();

const { goToNextStep } = stepStore;

const {
  isSaveSubscriptionInProgress,
  isGetSubscriptionInProgress,
  isMsbSelected,
  getCurrentDirectDebit,
  getBudgetPrices,
} = storeToRefs(subscriptionStore);

const isBudgetDetailModalOpen = ref(false);

const currentMonthlyPayment = ref<number | undefined>(
  getCurrentDirectDebit.value?.selectedLinearMonthlyPayment ?? undefined
);

const isRealTimeAvailable = isMsbSelected.value;

const directDebitModeCards = [
  {
    id: EDirectDebitMode.REALTIME.toLocaleLowerCase(),
    value: EDirectDebitMode.REALTIME,
    title: t('subscription.billingMode.linearOrRealTime.realTime.label'),
    description: t('subscription.billingMode.linearOrRealTime.realTime.description'),
    disabled: !isRealTimeAvailable,
  },
  {
    id: EDirectDebitMode.LINEAR.toLocaleLowerCase(),
    value: EDirectDebitMode.LINEAR,
    title: t('subscription.billingMode.linearOrRealTime.linear.label'),
    description: t('subscription.billingMode.linearOrRealTime.linear.description'),
  },
];

const getDefaultDebitMode = () => {
  if (isRealTimeAvailable) {
    return getCurrentDirectDebit.value?.mode ?? undefined;
  }
  return EDirectDebitMode.LINEAR;
};

const {
  value: currentDebitMode,
  errorMessage: debitModeError,
  validate: validateDebitMode,
} = useField<DirectDebitMode>(
  'directDebitMode',
  yup.string().required(t('common.forms.required')),
  {
    initialValue: getDefaultDebitMode(),
  }
);

const hasPaymentOptionsAvailable = computed(
  () => currentDebitMode.value === EDirectDebitMode.LINEAR
);

const showDirectDebitErrorToast = (error: unknown) => {
  addErrorToast({
    id: 'billing-mode',
    title: t('common.apiError'),
    content: resolveApiErrorMessage(error, {
      knownCodes: EDirectDebitErrorType,
      resolveCode: code => t(`subscription.billingMode.api.errors.${code}`),
      defaultMessage: t('subscription.billingMode.api.errors.patchSubscriptionDirectDebit'),
    }),
  });
};

const onSubmit = async () => {
  const { valid } = await validateDebitMode();
  if (!valid) return;

  try {
    await subscriptionStore.updateSubscriptionWithDirectDebit({
      mode: currentDebitMode.value,
      selectedLinearMonthlyPayment:
        currentDebitMode.value === EDirectDebitMode.LINEAR &&
        currentMonthlyPayment.value !== undefined
          ? currentMonthlyPayment.value
          : null,
    });
    await goToNextStep();
  } catch (error) {
    showDirectDebitErrorToast(error);
  }
};
</script>
